import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../supabase/supabase';
import { emailService } from '../../services/emailService';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { SRI_LANKA_DISTRICTS, SRI_LANKA_CITIES } from '../../utils/locations';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const { FiUser, FiScissors, FiClock, FiCheck, FiArrowRight, FiCamera, FiPlus, FiX, FiAlertTriangle } = FiIcons;

const STEPS = [
  { id: 'profile', title: 'Professional Profile', icon: FiUser },
  { id: 'services', title: 'Your Services', icon: FiScissors },
  { id: 'availability', title: 'Active Hours', icon: FiClock },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_SERVICES = [
  { name: 'Classic Haircut', price: '1500', duration: '45 min', selected: true },
  { name: 'Beard Trim', price: '800', duration: '20 min', selected: false },
  { name: 'The Executive Package', price: '3500', duration: '75 min', selected: false },
];

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Form States
  const [bio, setBio] = useState('');
  const [phone, setPhone] = useState('');
  
  // District + Service Cities
  const [district, setDistrict] = useState('');
  const [districtSearch, setDistrictSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [serviceCities, setServiceCities] = useState([]);
  const [barberCitySearch, setBarberCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [schedule, setSchedule] = useState(
    DAYS.reduce((acc, day) => ({ ...acc, [day]: { enabled: true, start: '09:00', end: '18:00' } }), {})
  );

  const { toast, showToast, hideToast } = useToast();

  const filteredDistricts = SRI_LANKA_DISTRICTS.filter(d => 
    d.toLowerCase().includes(districtSearch.toLowerCase()) && d !== district
  );

  const availableCities = district ? (SRI_LANKA_CITIES[district] || []).filter(
    c => c.toLowerCase().includes(barberCitySearch.toLowerCase()) && !serviceCities.includes(c)
  ) : [];

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) navigate('/login');
      setUser(user);

      const { data: profileData } = await supabase
        .from('profiles_1740423033529')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileData) {
        setProfile(profileData);
        if (profileData.phone) setPhone(profileData.phone);
        if (profileData.bio) setBio(profileData.bio);
        if (profileData.avatar_url) setAvatarPreview(profileData.avatar_url);
        if (profileData.district) {
          setDistrict(profileData.district);
          setDistrictSearch(profileData.district);
        }
        if (profileData.service_cities?.length > 0) {
          setServiceCities(profileData.service_cities);
        }
      }
    };
    checkUser();
  }, [navigate]);

  const handleSwitchAccount = async () => {
    setLoading(true);
    try {
      await supabase.from('profiles_1740423033529').update({ role: 'customer' }).eq('id', user.id);
      navigate('/app/onboarding');
    } catch (err) {
      showToast("Error switching account type.", "error");
      setLoading(false);
      setShowSwitchModal(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'CONFIRM') return;
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_own_account');
      if (error) throw error;
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      showToast("Failed to delete account: " + err.message, "error");
      setDeleting(false);
    }
  };

  const handleAvatarClick = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const updateTime = (day, field, value) => {
    setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const handleSelectDistrict = (d) => {
    setDistrict(d);
    setDistrictSearch(d);
    setServiceCities([]);
    setBarberCitySearch('');
    setShowDistrictDropdown(false);
  };

  const handleAddCity = (city) => {
    setServiceCities(prev => [...prev, city]);
    setBarberCitySearch('');
    setShowCityDropdown(false);
  };

  const handleRemoveCity = (city) => {
    setServiceCities(prev => prev.filter(c => c !== city));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const { error: profileError } = await supabase
        .from('profiles_1740423033529')
        .update({
          bio,
          phone,
          district,
          service_districts: district ? [district] : [],
          service_cities: serviceCities,
          avatar_url: avatarPreview,
          onboarding_completed: true,
          is_available: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      const selectedServices = services.filter(s => s.selected).map(s => ({
        name: s.name,
        price: Number(s.price),
        duration: s.duration,
        barber_id: user.id
      }));

      if (selectedServices.length > 0) {
        const { error: servicesError } = await supabase
          .from('services_1740423033529')
          .insert(selectedServices);
        if (servicesError) throw servicesError;
      }

      if (profile && !profile.welcome_sent) {
        await emailService.sendWelcomeEmail(profile.full_name, 'barber', profile.email);
        await supabase.from('profiles_1740423033529').update({ welcome_sent: true }).eq('id', user.id);
      }

      navigate('/barber/dashboard');
    } catch (err) {
      console.error(err);
      showToast('Error saving profile: ' + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 0) {
      if (!phone || phone.length < 9) {
        showToast("Please provide a valid phone number.", "error");
        return;
      }
      if (!district) {
        showToast("Please select your district.", "error");
        return;
      }
      if (serviceCities.length === 0) {
        showToast("Please select at least one service city.", "error");
        return;
      }
    }
    
    if (currentStep === 1) {
      const hasSelected = services.some(s => s.selected);
      if (!hasSelected) {
        showToast("Please select at least one service to offer.", "error");
        return;
      }
    }

    if (currentStep < STEPS.length - 1) setCurrentStep(prev => prev + 1);
    else handleComplete();
  };

  return (
    <div className="min-h-screen bg-brand-light flex flex-col items-center justify-center p-4 sm:p-8 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <div className="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[800px]">
        {/* Banner */}
        <div className="w-full bg-brand-dark p-4 text-center border-b border-white/10 flex justify-between items-center px-8 flex-shrink-0 shadow-inner">
          <span className="text-xs font-black text-gray-300 uppercase tracking-wider">Just looking for a haircut?</span>
          <button 
            onClick={() => setShowSwitchModal(true)} 
            className="text-xs font-black text-brand-accent bg-white/10 px-4 py-2 rounded-full hover:bg-white hover:text-brand-dark uppercase tracking-widest transition-all"
          >
            Switch to Customer
          </button>
        </div>

        {/* Header */}
        <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primary rounded-xl flex items-center justify-center text-white">
              <SafeIcon icon={STEPS[currentStep].icon} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Step {currentStep + 1} of 3</p>
              <h2 className="text-xl font-bold text-brand-black">{STEPS[currentStep].title}</h2>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === currentStep ? 'w-8 bg-brand-primary' : i < currentStep ? 'w-4 bg-brand-accent' : 'w-4 bg-gray-100'}`} />
              ))}
            </div>
            
            {/* Delete Account Button */}
            <button 
              onClick={() => setShowDeleteModal(true)}
              className="text-red-400 hover:text-red-600 transition-colors p-2 rounded-xl hover:bg-red-50"
              title="Delete Account"
            >
              <SafeIcon icon={FiAlertTriangle} className="text-lg" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 hide-scrollbar">
          <AnimatePresence mode="wait">
            {/* STEP 1: Profile */}
            {currentStep === 0 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                <div className="flex flex-col items-center mb-4">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                  <div onClick={handleAvatarClick} className="relative group cursor-pointer">
                    <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-200 group-hover:border-brand-primary transition-all overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiCamera} className="text-2xl text-gray-400 group-hover:text-brand-primary" />
                      )}
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-brand-primary text-white p-1.5 rounded-lg shadow-lg">
                      <SafeIcon icon={FiPlus} className="text-xs" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-3 font-bold uppercase tracking-wider text-center">Upload Professional Photo</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">Phone Number *</label>
                  <div className="flex bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-brand-primary overflow-visible transition-colors relative z-50">
                    <PhoneInput
                      defaultCountry="lk"
                      value={phone}
                      onChange={(p) => setPhone(p)}
                      className="w-full flex"
                      inputStyle={{ width: '100%', height: '56px', border: 'none', backgroundColor: 'transparent', fontWeight: '700', fontSize: '1rem', outline: 'none', paddingLeft: '12px' }}
                      countrySelectorStyleProps={{ buttonStyle: { height: '56px', border: 'none', backgroundColor: 'transparent', paddingLeft: '16px', paddingRight: '8px' } }}
                    />
                  </div>
                </div>

                {/* District Searchable Dropdown */}
                <div className="relative">
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">Your District *</label>
                  <div className="relative">
                    <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                    <input 
                      type="text" 
                      placeholder="Search district (e.g. Colombo, Kandy)..."
                      value={districtSearch}
                      onChange={(e) => {
                        setDistrictSearch(e.target.value);
                        setDistrict('');
                        setServiceCities([]);
                        setShowDistrictDropdown(true);
                      }}
                      onFocus={() => setShowDistrictDropdown(true)}
                      className="w-full pl-12 pr-10 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                    />
                    {district && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <SafeIcon name="CheckCircle" className="text-green-500" />
                      </div>
                    )}
                  </div>

                  {showDistrictDropdown && !district && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowDistrictDropdown(false)}></div>
                      <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2">
                        {filteredDistricts.length > 0 ? filteredDistricts.map(d => (
                          <div 
                            key={d} 
                            className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors"
                            onClick={() => handleSelectDistrict(d)}
                          >{d}</div>
                        )) : (
                          <div className="px-5 py-3 text-gray-400 text-sm italic">No districts found</div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Service Cities */}
                {district && (
                  <div className="relative animate-in fade-in slide-in-from-top-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">
                      Service Cities in {district} *
                    </label>
                    <div 
                      className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 flex flex-wrap gap-2 cursor-pointer focus-within:border-brand-primary transition-colors min-h-[56px]"
                      onClick={() => setShowCityDropdown(true)}
                    >
                      {serviceCities.map(c => (
                        <span key={c} className="bg-brand-primary text-white text-xs px-3 py-1 rounded-lg font-bold flex items-center gap-1">
                          {c}
                          <div className="ml-1 cursor-pointer hover:text-red-200" onClick={(e) => { e.stopPropagation(); handleRemoveCity(c); }}>
                            <SafeIcon icon={FiX} className="text-[10px]" />
                          </div>
                        </span>
                      ))}
                      <input 
                        type="text" 
                        placeholder={serviceCities.length === 0 ? "Search & select cities you service" : ""}
                        value={barberCitySearch}
                        onChange={e => {
                          setBarberCitySearch(e.target.value);
                          setShowCityDropdown(true);
                        }}
                        className="bg-transparent outline-none flex-1 min-w-[180px] font-bold text-brand-dark"
                      />
                    </div>

                    {showCityDropdown && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowCityDropdown(false)}></div>
                        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2">
                          {availableCities.length > 0 ? availableCities.map(c => (
                            <div 
                              key={c} 
                              className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors"
                              onClick={() => handleAddCity(c)}
                            >{c}</div>
                          )) : (
                            <div className="px-5 py-3 text-gray-400 text-sm italic">
                              {(SRI_LANKA_CITIES[district] || []).length === serviceCities.length ? 'All cities selected' : 'No cities found'}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {serviceCities.length > 0 && (
                      <p className="text-[10px] text-brand-primary font-black uppercase tracking-widest mt-1 pl-1">
                        {serviceCities.length} {serviceCities.length === 1 ? 'city' : 'cities'} selected
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">Your Professional Bio</label>
                  <textarea 
                    value={bio} 
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="E.g. Professional barber with 5 years experience. Specialized in modern fades and beard grooming."
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl h-28 outline-none focus:ring-4 focus:ring-brand-primary/5 focus:border-brand-primary transition-all text-sm leading-relaxed"
                  />
                </div>
              </motion.div>
            )}

            {/* STEP 2: Services — NO skip option, at least 1 required */}
            {currentStep === 1 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="bg-brand-primary/5 border border-brand-primary/10 rounded-2xl p-4 mb-2">
                  <p className="text-sm text-brand-primary font-black text-center">
                    Select at least one service to continue
                  </p>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest text-center mt-1">
                    You can customize prices in Rs. (LKR) from your dashboard later
                  </p>
                </div>

                {services.map((s, i) => (
                  <div 
                    key={i} 
                    onClick={() => {
                      const newS = [...services];
                      newS[i].selected = !newS[i].selected;
                      setServices(newS);
                    }}
                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${s.selected ? 'border-brand-primary bg-brand-primary/5' : 'border-gray-50 hover:border-gray-100'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${s.selected ? 'bg-brand-primary text-white' : 'bg-gray-100 text-transparent'}`}>
                        <SafeIcon icon={FiCheck} className="text-xs" />
                      </div>
                      <div>
                        <p className="font-bold text-brand-black">{s.name}</p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{s.duration}</p>
                      </div>
                    </div>
                    <p className="text-lg font-black text-brand-black">Rs. {Number(s.price).toLocaleString()}</p>
                  </div>
                ))}
                
                {!services.some(s => s.selected) && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-500 flex-shrink-0" />
                    <p className="text-xs font-bold text-red-600">Please select at least one service to continue.</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP 3: Availability */}
            {currentStep === 2 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
                <p className="text-sm text-gray-500 mb-4 font-medium text-center">Set your working hours for bookings across Sri Lanka.</p>
                {DAYS.map((day) => (
                  <div key={day} className="flex flex-col p-4 bg-gray-50 rounded-2xl border border-gray-100 gap-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => setSchedule(prev => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }))}
                          className={`w-10 h-5 rounded-full relative transition-colors ${schedule[day].enabled ? 'bg-brand-primary' : 'bg-gray-200'}`}
                        >
                          <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${schedule[day].enabled ? 'left-6' : 'left-1'}`} />
                        </button>
                        <span className={`text-sm font-bold ${schedule[day].enabled ? 'text-brand-black' : 'text-gray-400'}`}>{day}</span>
                      </div>
                    </div>
                    
                    {schedule[day].enabled && (
                      <div className="flex items-center gap-4 animate-in fade-in slide-in-from-top-1">
                        <div className="flex-1">
                          <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Start</label>
                          <input type="time" value={schedule[day].start} onChange={(e) => updateTime(day, 'start', e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg p-2 text-xs font-bold outline-none focus:border-brand-primary" />
                        </div>
                        <div className="flex-1">
                          <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">End</label>
                          <input type="time" value={schedule[day].end} onChange={(e) => updateTime(day, 'end', e.target.value)} className="w-full bg-white border border-gray-200 rounded-lg p-2 text-xs font-bold outline-none focus:border-brand-primary" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-8 bg-gray-50 border-t border-gray-100 flex gap-4 flex-shrink-0">
          {currentStep > 0 && (
            <Button variant="ghost" className="flex-1 border-gray-200 shadow-none hover:bg-white" onClick={() => setCurrentStep(prev => prev - 1)}>
              Back
            </Button>
          )}
          <Button className="flex-[2] py-4 bg-brand-black group" onClick={nextStep} disabled={loading}>
            {loading ? 'Saving...' : currentStep === STEPS.length - 1 ? 'Start Earning in LKR' : 'Next Step'}
            {!loading && <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />}
          </Button>
        </div>
      </div>

      {/* Switch Modal */}
      {showSwitchModal && (
        <div className="fixed inset-0 bg-brand-dark/80 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="User" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2">Switch to Customer?</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 leading-relaxed">
              Are you sure you want to switch from <strong>Barber</strong> to <strong>Customer</strong>?
            </p>
            <div className="flex flex-col gap-3">
              <Button className="w-full py-4 font-black" onClick={handleSwitchAccount} disabled={loading}>
                {loading ? 'Switching...' : 'Yes, I am a Customer'}
              </Button>
              <Button variant="ghost" className="w-full py-3 text-gray-500 font-bold" onClick={() => setShowSwitchModal(false)} disabled={loading}>
                No, Stay as Barber
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-brand-dark/80 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2">Delete Account?</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 leading-relaxed">
              This will <strong className="text-red-600">permanently delete</strong> your barber account and all data. Type <strong className="text-red-600">CONFIRM</strong> to proceed.
            </p>
            <input 
              type="text" 
              placeholder="Type CONFIRM"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-center font-black outline-none focus:border-red-500"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => {setShowDeleteModal(false); setDeleteConfirmText('');}}>
                Cancel
              </Button>
              <Button variant="danger" className="flex-1" disabled={deleteConfirmText !== 'CONFIRM' || deleting} onClick={handleDeleteAccount}>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}